import React from "react";
import styles from "../../styles/ContentList.module.scss";
import { CONTENT_DEEPLINK_ACTIONS } from "../../store/constants";
import { hitContentDeeplinkNew, hitContentDeeplinkOld } from "../../helpers/hitContentDeeplink";
import hitStoragePermissionRequiredAction from "../../helpers/handleStoragePermissions";
import formatPracticeTestDateTime from "../../helpers/formatPracticeTestDateTime";

const toEpochMs = (time) => {
    if (time == null || time === "") return null;
    const numeric = Number(time);
    if (!Number.isNaN(numeric) && String(time).trim() !== "") {
        const digits = String(Math.trunc(Math.abs(numeric))).length;
        return digits === 10 ? numeric * 1000 : numeric;
    }
    const parsed = new Date(time).getTime();
    return Number.isNaN(parsed) ? null : parsed;
};

const MoreIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="12" cy="12" r="12" fill="#7A8B94" />
        <circle cx="12" cy="7.25" r="1.75" fill="#fff" />
        <circle cx="12" cy="12" r="1.75" fill="#fff" />
        <circle cx="12" cy="16.75" r="1.75" fill="#fff" />
    </svg>
);

const PracticeTestContent = ({ content }) => {
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const menuRef = React.useRef(null);

    const isAttempted = Number(content?.isAttempted) === 1;
    const remaining = Number(content?.numberOfAttemptsRemaining);
    const total = Number(content?.totalAttempts);
    const hasAttemptCounts = content?.numberOfAttemptsRemaining != null && content?.totalAttempts != null;
    const startTime = toEpochMs(content?.startTime);
    const endTime = toEpochMs(content?.endTime);
    const hasNotStarted = startTime != null && Date.now() < startTime;
    const isTestClosed = endTime != null && Date.now() >= endTime;

    React.useEffect(() => {
        if (!isMenuOpen) return;
        const handleClickOutside = (event) => {
            if (!menuRef.current?.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isMenuOpen]);

    const hitAttemptDeeplink = (contentObj, action) => {
        hitContentDeeplinkNew(
            action,
            contentObj,
            true,
            true,
            contentObj.URL
        );
    };

    const handleAttempt = (e) => {
        e?.stopPropagation();
        setIsMenuOpen(false);
        hitStoragePermissionRequiredAction(
            (contentObj) => hitAttemptDeeplink(contentObj, CONTENT_DEEPLINK_ACTIONS.ACTION_TEST_ATTEMPT),
            content
        );
    };

    const handleReattempt = (e) => {
        e?.stopPropagation();
        setIsMenuOpen(false);
        hitStoragePermissionRequiredAction(
            (contentObj) => hitAttemptDeeplink(contentObj, CONTENT_DEEPLINK_ACTIONS.ACTION_TEST_REATTEMPT),
            content
        );
    };

    const handleViewReport = (e) => {
        e?.stopPropagation();
        setIsMenuOpen(false);
        if (!content?.reportURL) return;
        hitContentDeeplinkOld(CONTENT_DEEPLINK_ACTIONS.UTIL_TEST, {
            paramOne: content.reportURL,
        });
    };

    const renderViewReportButton = () => (
        <button type="button" className={styles.contentList__item__content__practiceTest__button} onClick={handleViewReport}>
            View Report
        </button>
    );

    const renderAction = () => {
        if (hasNotStarted) return null;

        if (isTestClosed) {
            return isAttempted ? renderViewReportButton() : null;
        }

        if (total === 1 && isAttempted) {
            return renderViewReportButton();
        }

        if (remaining === 0 && isAttempted) {
            return renderViewReportButton();
        }

        if ((isAttempted && remaining > 0) || (isAttempted && remaining === -1 && total === -1)) {
            return (
                <div
                    className={styles.contentList__item__content__practiceTest__menu}
                    ref={menuRef}
                    onClick={(e) => e.stopPropagation()}
                >
                    {isMenuOpen ? (
                        <div className={styles.contentList__item__content__practiceTest__popup} role="menu">
                            <button type="button" className={styles.contentList__item__content__practiceTest__button} onClick={handleReattempt}>
                                Reattempt
                            </button>
                            <button type="button" className={styles.contentList__item__content__practiceTest__button} onClick={handleViewReport}>
                                View Report
                            </button>
                        </div>
                    ) : null}
                    <button
                        type="button"
                        className={styles.contentList__item__content__practiceTest__more}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsMenuOpen((open) => !open);
                        }}
                        aria-label="More actions"
                        aria-expanded={isMenuOpen}
                    >
                        <MoreIcon />
                    </button>
                </div>
            );
        }

        if (hasAttemptCounts && remaining === total) {
            return (
                <button type="button" className={styles.contentList__item__content__practiceTest__button} onClick={handleAttempt}>
                    Attempt
                </button>
            );
        }

        return null;
    };

    const reaminingAttempts = (content) => {
        if(content?.totalAttempts === -1) {
            return <p>Attempts: Unlimited </p>
        }
        if(content?.numberOfAttemptsRemaining > 0) {
            return <p>Attempts: {content?.numberOfAttemptsRemaining} </p>
        }
        return null;
    }


    const formatTimeLabel = (time) => {
        const formatted = formatPracticeTestDateTime(time);
        if (!formatted || formatted === "Invalid date") return "";
        return formatted;
    };
    const startLabel = formatTimeLabel(content?.startTime) || formatTimeLabel(content?.createdAt);
    const endLabel = formatTimeLabel(content?.endTime);

    return (
        <div className={styles.contentList__item__content__practiceTest}>
            {(startLabel || endLabel) && (
                <div className={styles.contentList__item__content__practiceTest__timeDiv} >
                    {startLabel && <p>{startLabel}</p>}
                    {endLabel && <p>{endLabel}</p>}
                    {reaminingAttempts(content)}
                </div>
            )}
        {renderAction()}
        </div>
    );
};

export default PracticeTestContent;
